import React, { useEffect, useCallback, useMemo } from 'react'
import MainLayout from '../../../layouts/MainLayout'
import { usePermissions } from '../../../hooks/usePermissions'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  faEdit,
  faTrash,
  faEye,
  faUndo,
} from '@fortawesome/free-solid-svg-icons'
import ConfirmationModal from '../../../components/modals/ConfirmationModal'
import useLoader from '../../../hooks/useLoader'
import useNotification from '../../../hooks/useNotification'
import { maskCpf, maskCnpj } from '../../../utils/maskUtils'
import useBaseService from '../../../hooks/services/useBaseService'
import { entities } from '../../../constants/entities'
import PageHeader from '../../../components/PageHeader'
import ListHeader from '../../../components/ListHeader'
import useAction from '../../../hooks/useAction'
import useUrlFilters from '../../../hooks/useUrlFilters'
import useGenericFilter from '../../../hooks/filters/useGenericFilter'
import { transformFiltersForUrl } from '../../../utils/transformFiltersForUrl'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import TableWithInactiveFilter from '../../../components/table/TableWithInactiveFilter'
import FilterCollapse from '../../../components/FilterCollapse'
import { inputsFiltersSuppliers } from './filters/filters-suppliers'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'

const SuppliersPage = () => {
  const { canAccess } = usePermissions()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { showLoader, hideLoader } = useLoader()
  const { showNotification } = useNotification()
  const { get: baseServiceGet, del: baseServiceDel } = useBaseService(navigate)

  // Padronização de filtros via useUrlFilters
  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      alias: [],
      name: [],
      cpf_cnpj: [],
      deleted_at: false,
    },
    'almox_suppliers'
  )

  // --- Lógica de busca de fornecedores com TanStack Query ---
  const {
    data: suppliersData,
    isLoading,
    isFetching,
    isError: isErrorSuppliers,
    error: suppliersError,
  } = useQueryWithLoader({
    queryKey: ['almox_suppliers', filters, pagination],
    queryFn: async () => {
      const filtersTranformed = transformFiltersForUrl(filters)
      const params = {
        filters: filtersTranformed,
        page: pagination.page,
        perPage: pagination.perPage,
      }
      const response = await baseServiceGet(entities.suppliers.get, params)
      return response.result
    },
    staleTime: 60 * 1000, // Cacheia os dados por 60 segundos
    select: (data) => {
      return {
        suppliers: data.data.map((supplier) => {
          const numericValue = supplier.cpf_cnpj
            ? supplier.cpf_cnpj.replace(/\D/g, '')
            : ''
          return {
            id: supplier.id,
            alias: supplier.alias,
            name: supplier.name,
            cpf_cnpj:
              numericValue.length <= 11
                ? maskCpf(numericValue)
                : maskCnpj(numericValue),
            in_transaction: supplier.in_transaction,
            isDeleted: !!supplier.deleted_at, // Booleano para controle de ações
          }
        }),
        totalPages: data.last_page,
      }
    },
  })

  const { suppliers = [], totalPages = 1 } = suppliersData || {}

  const columns = [
    { key: 'alias', label: 'Apelido' },
    { key: 'name', label: 'Nome' },
    { key: 'cpf_cnpj', label: 'CPF/CNPJ' },
  ]

  // --- Gerenciamento de Loader com base nos estados do TanStack Query ---
  useEffect(() => {
    if (isLoading) {
      showLoader()
    } else {
      hideLoader()
    }
  }, [isLoading, showLoader, hideLoader])

  // --- Notificação de Erro para a Query de Fornecedores ---
  useEffect(() => {
    if (isErrorSuppliers) {
      console.error(
        'SuppliersPage: Erro ao carregar fornecedores (useQuery):',
        suppliersError
      )
      showNotification(
        'error',
        `Erro ao carregar fornecedores: ${
          suppliersError.response?.data?.message || suppliersError.message
        }`
      )
    }
  }, [isErrorSuppliers, suppliersError, showNotification])

  // --- Função para Forçar o Refetch da Lista (invalida o cache) ---
  const refreshList = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['almox_suppliers', filters, pagination],
    })
  }, [queryClient, filters, pagination])

  const {
    openModalConfirmation,
    action,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
  } = useAction(navigate)

  // Lógica de Aplicação e Limpeza de Filtros (via useGenericFilter)
  const handleFilterApply = useCallback(
    (currentLocalFilterValues) => {
      const transformed = transformFiltersForUrl(currentLocalFilterValues)
      applyUrlFilters(transformed)
    },
    [applyUrlFilters]
  )

  const handleFilterClear = useCallback(
    (initialLocalFilterState) => {
      const transformedInitial = transformFiltersForUrl(initialLocalFilterState)
      applyUrlFilters(transformedInitial)
    },
    [applyUrlFilters]
  )

  // Inicializa o useGenericFilter com o estado inicial dos filtros do formulário
  const { localFilterValues, handleFilterChange, handleSubmit, handleClear } =
    useGenericFilter(
      {
        alias: [],
        name: [],
        cpf_cnpj: [],
        deleted_at: false,
      },
      handleFilterApply,
      handleFilterClear
    )

  const inactiveSupplierMutation = useMutation({
    mutationFn: async (supplierId) => {
      showLoader()
      const response = await baseServiceDel(
        entities.suppliers.delete(supplierId)
      )
      return response
    },
    onSuccess: (res) => {
      if (res?.status === 200) refreshList()
    },
    onError: (err) => {
      console.log('onError ', err)
      hideLoader()
    },
    onSettled: () => {
      hideLoader()
      handleCancelConfirmation()
    },
  })

  const activateSupplierMutation = inactiveSupplierMutation

  // useEffect para mensagens de estado de localização (mantido)
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message)
      // Limpa o estado para que a mensagem não apareça novamente ao navegar
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, showNotification, navigate, location.pathname])

  const actions = useMemo(
    () => [
      {
        id: 'details',
        icon: faEye,
        title: 'Ver Detalhes',
        buttonClass: 'btn-info',
        permission: 'Ver fornecedores',
        onClick: (supplier) =>
          navigate(`/almoxarifado/fornecedores/detalhes/${supplier.id}`),
        show: (supplier) =>
          !supplier.isDeleted && canAccess('Ver fornecedores'),
      },
      {
        id: 'edit',
        icon: faEdit,
        title: 'Editar',
        buttonClass: 'btn-primary',
        permission: 'Atualizar fornecedores',
        onClick: (supplier) =>
          navigate(`/almoxarifado/fornecedores/editar/${supplier.id}`),
        show: (supplier) =>
          !supplier.isDeleted && canAccess('Atualizar fornecedores'),
      },
      {
        id: 'delete',
        icon: faTrash,
        title: 'Excluir',
        buttonClass: 'btn-danger',
        permission: 'Excluir fornecedores',
        onClick: (supplier) =>
          showConfirmation(
            supplier,
            `Você tem certeza que deseja excluir?`,
            async () => {
              try {
                await inactiveSupplierMutation.mutateAsync(supplier.id)
              } catch (error) {
                console.log('Erro ao excluir fornecedor:', {
                  status: error.status,
                  message: error.message,
                })
              }
            }
          ),
        show: (supplier) =>
          !supplier.isDeleted &&
          !supplier.in_transaction &&
          canAccess('Excluir fornecedores'),
      },
      {
        id: 'activate',
        icon: faUndo,
        title: 'Restaurar',
        buttonClass: 'btn-info',
        permission: 'Excluir fornecedores',
        onClick: (supplier) =>
          showConfirmation(
            supplier,
            `Você tem certeza que deseja restaurar?`,
            async () => {
              try {
                await activateSupplierMutation.mutateAsync(supplier.id)
              } catch (error) {
                console.log('Erro ao restaurar fornecedor:', {
                  status: error.status,
                  message: error.message,
                })
              }
            }
          ),
        show: (supplier) =>
          supplier.isDeleted && canAccess('Excluir fornecedores'),
      },
    ],
    [
      navigate,
      canAccess,
      showConfirmation,
      inactiveSupplierMutation,
      activateSupplierMutation,
    ]
  )

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Fornecedores"
        showBackButton={true}
        backUrl="/dashboard"
      />
      <div className="container-fluid p-1">
        <FilterCollapse
          autoCompleteFields={inputsFiltersSuppliers({
            localFilterValues,
            handleFilterChange,
          })}
          onSubmit={handleSubmit}
          onClear={handleClear}
        />

        <ListHeader
          title="Lista de Fornecedores"
          buttonText="Novo Fornecedor"
          buttonLink="/almoxarifado/fornecedores/criar"
          canAccess={canAccess}
          permission="Criar fornecedores"
        />

        <TableWithInactiveFilter
          data={suppliers}
          columns={columns}
          actions={actions}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value
            setPagination({ page: 1, perPage: newPerPage })
          }}
          filterValues={localFilterValues}
          localFilterValues={localFilterValues}
          applyUrlFilters={applyUrlFilters}
          type="default"
          isLoading={isLoading}
          isFetching={isFetching}
        />

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={selectedItem ? selectedItem.name : ''}
          text={action.text}
        />
      </div>
    </MainLayout>
  )
}

export default SuppliersPage
